import { PLANS } from '../../server/lib/constants'
import { COLLECTION_ADD_RESULTS_LIMIT, COLLECTION_PAPER_LIMITS } from '../../config/static/pricing-plans'
import { userIsAtLeast } from './user'

/**
 * The single source of truth for "what plan is this user on?".
 *
 * Plan precedence is subtle and used to be re-derived ad hoc at every call site,
 * which let two screens disagree — most visibly a Team subscriber showing as
 * "Pro" on /pricing (SCI-2765). All of the rules live here: pure, framework-free
 * and unit-tested. UI reads the named booleans below instead of reimplementing
 * the precedence (and getting it subtly wrong).
 *
 * @param user        the redux `ui.user` object
 * @param orgLicense  the redux `ui.organizationLicense` object
 */
export const resolvePlanState = (user, orgLicense) => {
  const plan = user?.subscription?.plan ?? PLANS.free
  // A canceled (inactive) sub owns no live plan, so it must not mark any tier as
  // current — every card stays purchasable so the user can re-subscribe.
  const hasCanceled = user?.subscription?.status === 'inactive'
  const hasLicense = !!orgLicense?.hasLicense
  // The backend has shipped this flag as both camelCase and snake_case.
  const isSelfServe = !!(orgLicense?.isSelfServe ?? orgLicense?.is_self_serve)
  // A non-self-serve org license is Enterprise/institutional: their plan floors
  // up to Pro/organizational, but they aren't a self-serve Pro/Team purchaser.
  const isEnterprise = hasLicense && !isSelfServe
  // A self-serve team surfaces two ways, and they have DIFFERENT cancel semantics:
  //
  //   1. Via the self-serve org license (members, and the admin). Access comes
  //      from the license, not the personal sub — and joining a team flips a
  //      member's own subscription to `inactive`. So this path must NOT honour the
  //      `hasCanceled` guard, or those members would lose their Team plan on the
  //      pricing page (SCI-2765 review). A lapsed license (status INACTIVE) no
  //      longer grants access, so it stops counting as Team.
  //   2. Via the solo `team` subscription plan (the buyer, before/without a
  //      provisioned license). That's a personal sub, so the cancel guard applies.
  //
  // PLAN_LEVELS.team === PLAN_LEVELS.pro, so userIsAtLeast(user, PLANS.pro) can
  // gate features but CANNOT tell Pro from Team — read the plan and license here.
  const hasTeamLicense = hasLicense && isSelfServe && orgLicense?.status !== 'INACTIVE'
  const isTeam = hasTeamLicense || (!hasCanceled && plan === PLANS.team)
  const isPro = !hasCanceled && !isEnterprise && !isTeam && userIsAtLeast(user, PLANS.pro)
  const isBasic = !hasCanceled && !isPro && !isTeam && userIsAtLeast(user, PLANS.basic)

  return {
    plan,
    hasCanceled,
    hasLicense,
    isSelfServe,
    isEnterprise,
    isTeam,
    isPro,
    isBasic,
    isTeamAdmin: !!orgLicense?.isTeamAdmin,
    // Admin contact on a non-self-serve (Enterprise/institutional) license. Derived
    // server-side in checkLicense from adminContacts, which never reaches the client.
    isEnterpriseAdmin: !!orgLicense?.isEnterpriseAdmin
  }
}

// How many papers this user's collections can hold in total (SCI-3863). Takes the
// object from resolvePlanState(); precedence follows its named booleans
// (Enterprise > Pro/Team > Basic > Free).
//
// Free gets a real entry rather than flooring to Basic, because Free users do
// reach these surfaces: CreateDashboardSearchResultsModal is opened from
// SearchDropdownMenu, which gates on `user?.email` alone and not on plan. The API
// lets a free user create ONE collection of up to 100 papers (the has_free_limit
// branch of lib/dashboards/validation.py), so flooring to Basic quoted them 1,000
// when 100 was the truth (PR #8487 review).
//
// An unresolvable plan state also lands on Free — under-promising is the safe
// direction when we cannot tell what someone is entitled to.
//
// CAVEAT: this models the per-collection cap but NOT the free tier's "one
// collection only" rule, so a Free user who already has one still gets a 400 no
// matter how small the search. See SCI-3864.
export const collectionPaperLimit = ({ isEnterprise, isPro, isTeam, isBasic } = {}) => {
  if (isEnterprise) {
    return COLLECTION_PAPER_LIMITS.enterprise
  }
  if (isPro || isTeam) {
    return COLLECTION_PAPER_LIMITS.pro
  }
  if (isBasic) {
    return COLLECTION_PAPER_LIMITS.basic
  }
  return COLLECTION_PAPER_LIMITS.free
}

// How many search results one "add to collection" can actually resolve. Two
// independent ceilings apply and the lower one wins:
//
//   1. the tier's own collection cap (Free 100, Basic 1,000), and
//   2. Elasticsearch's max result window, which truncates ANY single add at
//      10,000 no matter how generous the plan.
//
// So Enterprise reports 10,000 here, not its 50,000 total — that total is real,
// but it is reached across several adds. Quoting 50,000 on a single add would
// promise 40,000 papers that never arrive.
export const collectionResultsLimit = planState =>
  Math.min(collectionPaperLimit(planState), COLLECTION_ADD_RESULTS_LIMIT)

// API access + PAYG is a self-serve Pro/Team entitlement (SCI-3608).
// Enterprise/institutional (non-self-serve) licenses are intentionally excluded —
// their API access is negotiated separately. Takes the object from
// resolvePlanState(); `isPro` already excludes Team and Enterprise, so OR-ing in
// `isTeam` gives exactly "Pro or Team, not Enterprise".
export const planUnlocksApi = ({ isPro, isTeam } = {}) => !!(isPro || isTeam)

// Minting an MCP-scoped API token is available on every paid plan (SCI-3781).
// MCP works over OAuth on all plans; a static token is a low-friction
// alternative we don't gate behind Pro. Distinct from planUnlocksApi: general
// API access (all scopes, the full console) stays Pro/Team — paid non-Pro/Team
// plans only get the MCP-only minting path. Free stays gated. Takes the object
// from resolvePlanState().
export const planUnlocksMcpToken = ({ isBasic, isPro, isTeam, isEnterprise } = {}) =>
  !!(isBasic || isPro || isTeam || isEnterprise)
